import { useTheme } from '../context/ThemeContext';
import { Background3D } from '../components/Background3D';
import { 
  Code, 
  Terminal, 
  Zap, 
  Shield, 
  Cpu, 
  Database, 
  GitBranch,
  ChevronRight,
  AlertCircle 
} from 'lucide-react';

export function Huxley() {
  const { theme } = useTheme();

  return (
    <div className="relative">
      <Background3D />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h1 className="text-5xl md:text-7xl font-semibold mb-6 leading-tight">
              <span className="text-gradient">Huxley</span>
            </h1>
            <p className="text-2xl md:text-3xl font-medium mb-6">
              Biological Computational Engine
            </p>
            <p className={`text-lg leading-relaxed ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              A model-agnostic framework for long-horizon biological AI systems.
            </p>
          </div>

          {/* Quick Stats */}
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            {[
              { label: "LLM Providers", value: "5+" },
              { label: "Supported Models", value: "15+" },
              { label: "Built-in Tools", value: "20+" },
              { label: "Open Source", value: "MIT" }
            ].map((stat, index) => (
              <div
                key={index}
                className={`text-center p-6 rounded-xl ${
                  theme === 'dark'
                    ? 'bg-dark-200/50 border border-dark-300'
                    : 'bg-white/50 border border-gray-200'
                } backdrop-blur-sm`}
              >
                <div className="text-2xl font-semibold mb-2">{stat.value}</div>
                <div className={`text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20">
        <div className="max-w-[1440px] mx-auto px-6">
          <h2 className="text-4xl font-semibold mb-12 text-center">Overview</h2>
          <div className={`p-8 rounded-2xl ${
            theme === 'dark'
              ? 'bg-dark-200/50 border border-dark-300'
              : 'bg-white/50 border border-gray-200'
          } backdrop-blur-sm max-w-3xl mx-auto`}>
            <p className={`text-lg leading-relaxed ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Huxley provides foundational infrastructure for computational biology and AI-driven research. 
              The framework is designed to be modular, extensible, and provider-agnostic, enabling seamless 
              integration with multiple LLM providers and specialized biological tools.
            </p>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className={`py-20 ${
        theme === 'dark' ? 'bg-dark-200/30' : 'bg-gray-50/50'
      }`}>
        <div className="max-w-[1440px] mx-auto px-6">
          <h2 className="text-4xl font-semibold mb-12 text-center">Core Capabilities</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: Code,
                title: "LLM Abstraction Layer",
                description: "Seamlessly swap between providers (Anthropic, OpenAI, Google, Cohere, xAI) without code modifications"
              },
              {
                icon: Database,
                title: "Typed Tool Registry",
                description: "Schema-validated tool definitions with automatic documentation and type safety"
              },
              {
                icon: Cpu,
                title: "Agent Orchestration",
                description: "Multi-step reasoning with planning, execution, memory, and verification"
              },
              {
                icon: GitBranch,
                title: "Distributed Compute",
                description: "Worker pools for computationally intensive biological tasks"
              },
              {
                icon: Zap,
                title: "SE(3) Diffusion",
                description: "De novo protein structure generation via FrameDiff with constraint satisfaction"
              },
              {
                icon: Shield,
                title: "RCSB PDB Integration",
                description: "Direct programmatic access to the Protein Data Bank with advanced querying"
              }
            ].map((capability, index) => (
              <div
                key={index}
                className={`p-6 rounded-xl ${
                  theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white/70 border border-gray-200'
                }`}
              >
                <capability.icon className="w-8 h-8 text-primary-500 mb-4" />
                <h3 className="text-xl font-semibold mb-3">{capability.title}</h3>
                <p className={`text-sm leading-relaxed ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {capability.description}
                </p>
              </div>
            ))}
          </div>
          <div className={`mt-8 p-6 rounded-xl border ${
            theme === 'dark'
              ? 'bg-dark-200/50 border-dark-300'
              : 'bg-white/70 border border-gray-200'
          }`}>
            <div className="flex items-start space-x-4">
              <Zap className="w-6 h-6 text-primary-500 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold mb-2">Chemistry Tooling</h4>
                <p className={`text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  RDKit-powered molecular analysis and property calculation for drug discovery and chemical design
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Installation Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-semibold mb-12 text-center">Installation</h2>

          <div className="space-y-8">
            {/* From Source */}
            <div className={`p-8 rounded-xl ${
              theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white/70 border border-gray-200'
            }`}>
              <h3 className="text-xl font-semibold mb-4">From Source</h3>
              <div className={`p-4 rounded-lg font-mono text-sm ${
                theme === 'dark' ? 'bg-black/50' : 'bg-gray-900/10'
              } overflow-x-auto`}>
                <code className={theme === 'dark' ? 'text-green-400' : 'text-green-700'}>
                  pip install -e .
                </code>
              </div>
            </div>

            {/* With Dependencies */}
            <div className={`p-8 rounded-xl ${
              theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white/70 border border-gray-200'
            }`}>
              <h3 className="text-xl font-semibold mb-4">With Development Dependencies</h3>
              <div className={`p-4 rounded-lg font-mono text-sm ${
                theme === 'dark' ? 'bg-black/50' : 'bg-gray-900/10'
              } overflow-x-auto`}>
                <code className={theme === 'dark' ? 'text-green-400' : 'text-green-700'}>
                  pip install -e ".[dev]"
                </code>
              </div>
            </div>

            {/* GPU Support */}
            <div className={`p-8 rounded-xl ${
              theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white/70 border border-gray-200'
            }`}>
              <h3 className="text-xl font-semibold mb-4">With GPU Support</h3>
              <div className={`p-4 rounded-lg font-mono text-sm ${
                theme === 'dark' ? 'bg-black/50' : 'bg-gray-900/10'
              } overflow-x-auto`}>
                <code className={theme === 'dark' ? 'text-green-400' : 'text-green-700'}>
                  pip install -e ".[gpu]"
                </code>
              </div>
            </div>

            {/* Interactive Setup */}
            <div className={`p-8 rounded-xl ${
              theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white/70 border border-gray-200'
            }`}>
              <h3 className="text-xl font-semibold mb-4">Interactive Setup</h3>
              <div className={`p-4 rounded-lg font-mono text-sm ${
                theme === 'dark' ? 'bg-black/50' : 'bg-gray-900/10'
              } overflow-x-auto`}>
                <code className={theme === 'dark' ? 'text-green-400' : 'text-green-700'}>
                  huxley setup
                </code>
              </div>
              <p className={`text-sm mt-3 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                The setup wizard guides you through API key configuration, model selection, and optional database setup.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Start - CLI */}
      <section className={`py-20 ${
        theme === 'dark' ? 'bg-dark-200/30' : 'bg-gray-50/50'
      }`}>
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-semibold mb-12 text-center">Quick Start</h2>

          <div className="space-y-6">
            <h3 className="text-2xl font-semibold">Command-Line Interface</h3>

            <div className={`p-6 rounded-xl ${
              theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white/70 border border-gray-200'
            }`}>
              <div className="flex items-center space-x-2 mb-3">
                <Terminal className="w-5 h-5 text-primary-500" />
                <span className="font-semibold">Generate a protein structure</span>
              </div>
              <div className={`p-4 rounded-lg font-mono text-sm ${
                theme === 'dark' ? 'bg-black/50' : 'bg-gray-900/10'
              } overflow-x-auto`}>
                <code className={theme === 'dark' ? 'text-green-400' : 'text-green-700'}>
                  huxley generate -l 100 -d "alpha helical bundle"
                </code>
              </div>
            </div>

            <div className={`p-6 rounded-xl ${
              theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white/70 border border-gray-200'
            }`}>
              <div className="flex items-center space-x-2 mb-3">
                <Terminal className="w-5 h-5 text-primary-500" />
                <span className="font-semibold">Interactive chat with tools</span>
              </div>
              <div className={`p-4 rounded-lg font-mono text-sm ${
                theme === 'dark' ? 'bg-black/50' : 'bg-gray-900/10'
              } overflow-x-auto`}>
                <code className={theme === 'dark' ? 'text-green-400' : 'text-green-700'}>
                  huxley chat
                </code>
              </div>
            </div>

            <div className={`p-6 rounded-xl ${
              theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white/70 border border-gray-200'
            }`}>
              <div className="flex items-center space-x-2 mb-3">
                <Terminal className="w-5 h-5 text-primary-500" />
                <span className="font-semibold">Autonomous research mode</span>
              </div>
              <div className={`p-4 rounded-lg font-mono text-sm ${
                theme === 'dark' ? 'bg-black/50' : 'bg-gray-900/10'
              } overflow-x-auto`}>
                <code className={theme === 'dark' ? 'text-green-400' : 'text-green-700'}>
                  huxley research "Identify proteins that bind to insulin"
                </code>
              </div>
            </div>

            <div className={`p-6 rounded-xl ${
              theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white/70 border border-gray-200'
            }`}>
              <div className="flex items-center space-x-2 mb-3">
                <Terminal className="w-5 h-5 text-primary-500" />
                <span className="font-semibold">Extended autonomous exploration</span>
              </div>
              <div className={`p-4 rounded-lg font-mono text-sm ${
                theme === 'dark' ? 'bg-black/50' : 'bg-gray-900/10'
              } overflow-x-auto`}>
                <code className={theme === 'dark' ? 'text-green-400' : 'text-green-700'}>
                  huxley automate -t 2 -d drug_discovery
                </code>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Python API */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-semibold mb-12 text-center">Python API</h2>

          <div className={`p-8 rounded-xl ${
            theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white/70 border border-gray-200'
          }`}>
            <div className={`p-6 rounded-lg font-mono text-sm overflow-x-auto ${
              theme === 'dark' ? 'bg-black/50' : 'bg-gray-900/10'
            }`}>
              <code className={theme === 'dark' ? 'text-green-400' : 'text-green-700'}>
                {`import asyncio
from huxley.agents import Agent
from huxley.core.types import AgentConfig

async def main():
    config = AgentConfig(
        name="bio-agent",
        model="claude-4.5-sonnet",
        provider="anthropic",
        system_prompt="You are a computational biology assistant.",
        tools=["pdb_search", "pdb_get_entry", "calculate_properties"],
        max_iterations=10,
    )
    
    agent = Agent(config)
    ctx = await agent.run("Analyse the binding site of PDB entry 1ABC.")
    print(agent.get_final_response(ctx))

asyncio.run(main())`}
              </code>
            </div>
          </div>
        </div>
      </section>

      {/* Supported Providers */}
      <section className={`py-20 ${
        theme === 'dark' ? 'bg-dark-200/30' : 'bg-gray-50/50'
      }`}>
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-semibold mb-12 text-center">Supported LLM Providers</h2>

          <div className="space-y-4">
            {[
              {
                provider: "Anthropic",
                models: "Claude 4.5 Opus, Sonnet, Haiku",
                specialization: "Complex reasoning, analysis"
              },
              {
                provider: "OpenAI",
                models: "GPT-5.2 Pro, GPT-5.2",
                specialization: "Multi-step tasks, structured outputs"
              },
              {
                provider: "Google",
                models: "Gemini 3 Pro, Flash",
                specialization: "Advanced planning, multimodal"
              },
              {
                provider: "Cohere",
                models: "Command A, Reasoning, Vision",
                specialization: "RAG, chain-of-thought, vision"
              },
              {
                provider: "xAI",
                models: "Grok 4",
                specialization: "Agentic research, real-time data"
              }
            ].map((item, index) => (
              <div
                key={index}
                className={`p-6 rounded-xl ${
                  theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white/70 border border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">{item.provider}</h3>
                    <p className={`text-sm ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      <strong>Models:</strong> {item.models}
                    </p>
                    <p className={`text-sm mt-1 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      <strong>Specialization:</strong> {item.specialization}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-primary-500 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Architecture */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-semibold mb-12 text-center">Architecture</h2>

          <div className={`p-8 rounded-xl ${
            theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white/70 border border-gray-200'
          }`}>
            <div className={`p-6 rounded-lg font-mono text-sm overflow-x-auto ${
              theme === 'dark' ? 'bg-black/50' : 'bg-gray-900/10'
            }`}>
              <code className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                {`huxley/
├── agents/         # Agent implementation and orchestration
├── api/            # FastAPI server with OpenAI-compatible endpoints
├── cli/            # Command-line interface
├── compute/        # Distributed worker infrastructure
├── core/           # Types, configuration, logging, exceptions
├── llm/            # LLM abstraction layer
│   └── providers/  # Anthropic, OpenAI, Google, Cohere, xAI
├── memory/         # Conversation and state persistence
├── prompts/        # Model-specific optimised system prompts
├── tools/          # Tool registry and execution
│   ├── biology/    # PDB, diffusion, structure tools
│   └── chemistry/  # RDKit molecular tools
├── verification/   # Output validation layer
└── visualisation/  # 3Dmol.js molecular viewer`}
              </code>
            </div>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <div className={`p-6 rounded-xl ${
              theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white/70 border border-gray-200'
            }`}>
              <h3 className="font-semibold mb-4">Design Principles</h3>
              <ul className="space-y-3">
                {[
                  "Model Agnosticism — No hardcoded model assumptions",
                  "Explicit Interfaces — Clear contracts between components",
                  "Stateless Services — Designed for horizontal scalability",
                  "Verification First — All outputs must be validated",
                  "Audit Everything — Structured logging and tracing"
                ].map((principle, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <ChevronRight className="w-4 h-4 text-primary-500 flex-shrink-0 mt-1" />
                    <span className={`text-sm ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {principle}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className={`p-6 rounded-xl ${
              theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white/70 border border-gray-200'
            }`}>
              <h3 className="font-semibold mb-4">Key Components</h3>
              <ul className="space-y-3">
                {[
                  "LLMClient — Provider-agnostic LLM access",
                  "ToolRegistry — Typed tool management",
                  "Agent — Multi-step reasoning loop",
                  "MemoryStore — Persistent state storage",
                  "WorkerPool — Distributed task execution"
                ].map((component, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <ChevronRight className="w-4 h-4 text-primary-500 flex-shrink-0 mt-1" />
                    <span className={`text-sm ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {component}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Protein Structure Generation */}
      <section className={`py-20 ${
        theme === 'dark' ? 'bg-dark-200/30' : 'bg-gray-50/50'
      }`}>
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-semibold mb-8 text-center">Protein Structure Generation</h2>
          <p className={`text-lg text-center max-w-2xl mx-auto mb-12 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Huxley includes SE(3) diffusion-based tools for de novo protein structure generation using 
            denoising diffusion probabilistic models (DDPMs).
          </p>

          <div className="space-y-6">
            <div className={`p-8 rounded-xl ${
              theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white/70 border border-gray-200'
            }`}>
              <h3 className="text-xl font-semibold mb-4">Available Tools</h3>
              <div className="space-y-3">
                {[
                  { tool: "generate_protein_structure", desc: "Unconditional or text-guided generation" },
                  { tool: "scaffold_protein_motif", desc: "Generate scaffolds around fixed motifs" },
                  { tool: "design_protein_binder", desc: "Design proteins that bind to targets" },
                  { tool: "generate_symmetric_assembly", desc: "Generate symmetric assemblies (cages, etc.)" },
                  { tool: "validate_protein_structure", desc: "Validate physical plausibility" }
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Code className="w-4 h-4 text-primary-500 flex-shrink-0 mt-1" />
                    <div>
                      <span className="font-mono font-semibold text-sm">{item.tool}</span>
                      <p className={`text-sm ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={`p-8 rounded-xl ${
              theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white/70 border border-gray-200'
            }`}>
              <h3 className="text-xl font-semibold mb-4">Example Usage</h3>
              <div className={`p-6 rounded-lg font-mono text-sm overflow-x-auto ${
                theme === 'dark' ? 'bg-black/50' : 'bg-gray-900/10'
              }`}>
                <code className={theme === 'dark' ? 'text-green-400' : 'text-green-700'}>
                  {`from huxley import generate_protein_structure

result = await generate_protein_structure(
    target_length=100,
    num_samples=3,
    conditioning_text="alpha helical bundle",
    diffusion_steps=200,
    guidance_scale=3.0,
)

for struct in result["structures"]:
    print(f"Generated: {struct['id']}")`}
                </code>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chemistry Tools */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-semibold mb-8 text-center">Chemistry Tools</h2>
          <p className={`text-lg text-center max-w-2xl mx-auto mb-12 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            RDKit-powered molecular analysis integrated into the tool registry.
          </p>

          <div className={`p-8 rounded-xl ${
            theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white/70 border border-gray-200'
          }`}>
            <div className={`p-6 rounded-lg font-mono text-sm overflow-x-auto ${
              theme === 'dark' ? 'bg-black/50' : 'bg-gray-900/10'
            }`}>
              <code className={theme === 'dark' ? 'text-green-400' : 'text-green-700'}>
                {`from huxley.tools.chemistry.molecules import (
    calculate_properties,
    validate_smiles,
    check_drug_likeness,
)

# Calculate molecular properties
props = calculate_properties("CC(=O)OC1=CC=CC=C1C(=O)O")

# Validate SMILES notation
is_valid = validate_smiles("CCO")

# Check Lipinski's Rule of Five
druglike = check_drug_likeness("CC(=O)OC1=CC=CC=C1C(=O)O")`}
              </code>
            </div>
          </div>
        </div>
      </section>

      {/* Environment Variables */}
      <section className={`py-20 ${
        theme === 'dark' ? 'bg-dark-200/30' : 'bg-gray-50/50'
      }`}>
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-semibold mb-12 text-center">Configuration</h2>

          <div className="space-y-6">
            <div className={`p-8 rounded-xl ${
              theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white/70 border border-gray-200'
            }`}>
              <h3 className="text-xl font-semibold mb-4">Environment Variables</h3>
              <div className={`p-6 rounded-lg font-mono text-sm overflow-x-auto ${
                theme === 'dark' ? 'bg-black/50' : 'bg-gray-900/10'
              }`}>
                <code className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                  {`# LLM Providers
export ANTHROPIC_API_KEY=sk-ant-...
export OPENAI_API_KEY=sk-...
export COHERE_API_KEY=...
export GOOGLE_API_KEY=...

# Server
export HUXLEY_SERVER__HOST=0.0.0.0
export HUXLEY_SERVER__PORT=8000

# Logging
export HUXLEY_LOG__LEVEL=INFO
export HUXLEY_LOG__FORMAT=json`}
                </code>
              </div>
            </div>

            <div className={`p-8 rounded-xl ${
              theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white/70 border border-gray-200'
            }`}>
              <h3 className="text-xl font-semibold mb-4">CLI Configuration</h3>
              <div className="space-y-3">
                {[
                  "huxley config show — View current configuration",
                  "huxley config set <key> <value> — Set configuration values",
                  "huxley config init-db — Initialize database",
                  "huxley config check-db — Verify database connection"
                ].map((cmd, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Terminal className="w-4 h-4 text-primary-500 flex-shrink-0 mt-1" />
                    <span className={`text-sm font-mono ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {cmd}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Development & License */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            <div className={`p-8 rounded-xl ${
              theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white/70 border border-gray-200'
            }`}>
              <h3 className="text-xl font-semibold mb-6">Development</h3>
              <div className="space-y-4">
                <div>
                  <p className="font-semibold text-sm mb-2">Install Development Dependencies</p>
                  <div className={`p-3 rounded font-mono text-xs ${
                    theme === 'dark' ? 'bg-black/50' : 'bg-gray-900/10'
                  } overflow-x-auto`}>
                    <code className={theme === 'dark' ? 'text-green-400' : 'text-green-700'}>
                      pip install -e ".[dev]"
                    </code>
                  </div>
                </div>
                <div>
                  <p className="font-semibold text-sm mb-2">Run Tests</p>
                  <div className={`p-3 rounded font-mono text-xs ${
                    theme === 'dark' ? 'bg-black/50' : 'bg-gray-900/10'
                  } overflow-x-auto`}>
                    <code className={theme === 'dark' ? 'text-green-400' : 'text-green-700'}>
                      pytest
                    </code>
                  </div>
                </div>
                <div>
                  <p className="font-semibold text-sm mb-2">Type Checking & Linting</p>
                  <div className={`p-3 rounded font-mono text-xs ${
                    theme === 'dark' ? 'bg-black/50' : 'bg-gray-900/10'
                  } overflow-x-auto space-y-1`}>
                    <div>
                      <code className={theme === 'dark' ? 'text-green-400' : 'text-green-700'}>
                        mypy src/huxley
                      </code>
                    </div>
                    <div>
                      <code className={theme === 'dark' ? 'text-green-400' : 'text-green-700'}>
                        ruff check src/huxley
                      </code>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={`p-8 rounded-xl ${
              theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white/70 border border-gray-200'
            }`}>
              <h3 className="text-xl font-semibold mb-6">License</h3>
              <p className={`text-lg font-semibold mb-4 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                MIT
              </p>
              <p className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Huxley is open source and available under the MIT license, enabling both commercial and 
                personal use with minimal restrictions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-20 ${
        theme === 'dark' ? 'bg-dark-200/30' : 'bg-gray-50/50'
      }`}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-semibold mb-6">
            Get Started with <span className="text-gradient">Huxley</span>
          </h2>
          <p className={`text-lg mb-8 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Build powerful biological AI systems with our comprehensive framework.
          </p>
          <button className="inline-flex items-center px-8 py-4 text-lg text-white bg-primary-600 rounded-full hover:bg-primary-700 transition-all duration-300 shadow-soft hover:shadow-lg">
            View Documentation
            <ChevronRight className="ml-2 w-5 h-5" />
          </button>
        </div>
      </section>
    </div>
  );
}
